class LabCertificates {
    constructor(element) {
        this.paginationObserver = null;
        this.searchAbortController = new AbortController();
        this.tableShowMorePriorState = new Map();
        this.element = element;
        this.domDelegateInstance = new window.domDelegate.Delegate(this.element);
        this.resultsContainer = this.element.querySelector('[data-element="results"]');
        this.searchInput = this.element.querySelector('[data-element="search-input"]');
        this.searchResetContainer = this.element.querySelector('[data-element="clear-search-container"]');
        this.initialResultsHtml = this.resultsContainer?.innerHTML ?? null;
        this.debouncedSearch = this.debounce((q) => this.performSearch(q), LabCertificates.DEBOUNCE_DELAY_MS);
        // Sort listener + initial sorting: always applicable (product detail page and overview page)
        this.attachSortListener();
        this.initializeSortableTables();
        this.attachTableSearchListeners();
        // Search, pagination and URL sync are only relevant on the overview page shell
        // (where the section provides the results container and search input)
        if (this.searchInput && this.resultsContainer) {
            this.attachSearchListeners();
            this.updateResetButtonVisibility();
            this.initializePaginationObserver();
            this.applyInitialSearchFromUrl();
        }
    }
    applyInitialSearchFromUrl() {
        if (!this.searchInput)
            return;
        const params = new URLSearchParams(location.search);
        const initialQuery = params.get('q');
        if (!initialQuery)
            return;
        this.searchInput.value = initialQuery;
        this.updateResetButtonVisibility();
        this.performSearch(initialQuery);
    }
    persistSearchInUrl(query) {
        const url = new URL(location.href);
        if (query === '') {
            url.searchParams.delete('q');
        }
        else {
            url.searchParams.set('q', query);
        }
        history.replaceState(null, '', url.toString());
    }
    get productList() {
        return this.resultsContainer?.querySelector('[data-element="product-list"]') ?? null;
    }
    attachSortListener() {
        this.domDelegateInstance.on('click', '[data-action="toggle-lab-certificate-sort"]', (e) => {
            const button = e.target.closest('[data-action="toggle-lab-certificate-sort"]');
            if (!button)
                return;
            const targetId = button.getAttribute('data-sort-target');
            if (!targetId)
                return;
            const table = this.element.querySelector(`[data-lab-certificates-table="${targetId}"]`);
            if (!table)
                return;
            const nextDirection = table.getAttribute('data-sort-direction') === 'asc' ? 'desc' : 'asc';
            this.sortTable(table, nextDirection);
        });
    }
    attachSearchListeners() {
        if (!this.searchInput)
            return;
        this.searchInput.addEventListener('input', () => {
            const query = this.searchInput.value.trim();
            this.updateResetButtonVisibility();
            this.debouncedSearch(query);
        });
        this.domDelegateInstance.on('click', '[data-element="clear-search"]', () => {
            if (!this.searchInput || this.searchInput.value === '')
                return;
            this.searchInput.value = '';
            this.searchInput.focus();
            this.updateResetButtonVisibility();
            this.performSearch('');
        });
    }
    updateResetButtonVisibility() {
        if (!this.searchInput || !this.searchResetContainer)
            return;
        const hasValue = this.searchInput.value.trim() !== '';
        this.searchResetContainer.classList.toggle('none', !hasValue);
    }
    attachTableSearchListeners() {
        this.domDelegateInstance.on('input', '[data-lab-certificates-table-search]', (e) => {
            const input = e.target;
            this.handleTableSearchInput(input);
        });
        this.domDelegateInstance.on('click', '[data-element="clear-table-search"]', (e) => {
            const button = e.target.closest('[data-element="clear-table-search"]');
            if (!button)
                return;
            const container = button.closest('.relative');
            if (!container)
                return;
            const input = container.querySelector('[data-lab-certificates-table-search]');
            if (!input || input.value === '')
                return;
            input.value = '';
            input.focus();
            this.handleTableSearchInput(input);
        });
    }
    handleTableSearchInput(input) {
        const targetId = input.getAttribute('data-lab-certificates-table-search');
        if (!targetId)
            return;
        const table = this.element.querySelector(`[data-lab-certificates-table="${targetId}"]`);
        if (!table)
            return;
        const query = input.value.trim();
        this.updateTableSearchClearVisibility(input, query);
        this.applyTableFilter(table, targetId, query);
    }
    updateTableSearchClearVisibility(input, query) {
        const container = input.parentElement?.querySelector('[data-element="clear-table-search-container"]');
        container?.classList.toggle('none', query === '');
    }
    applyTableFilter(table, showMoreId, query) {
        const normalizedQuery = query.toLowerCase();
        const isActive = normalizedQuery !== '';
        const rows = Array.from(table.querySelectorAll('[data-lab-certificate-row]'));
        let matchCount = 0;
        rows.forEach((row) => {
            const batchCell = row.children[1];
            const batchText = batchCell?.textContent?.trim().toLowerCase() ?? '';
            const matches = !isActive || batchText.includes(normalizedQuery);
            row.classList.toggle('none', !matches);
            if (matches)
                matchCount++;
        });
        const emptyState = this.element.querySelector(`[data-lab-certificates-table-empty="${showMoreId}"]`);
        const showEmpty = isActive && matchCount === 0;
        emptyState?.classList.toggle('none', !showEmpty);
        table.classList.toggle('none', showEmpty);
        const showMoreWrapper = document.getElementById(showMoreId);
        if (!showMoreWrapper)
            return;
        const button = showMoreWrapper.querySelector('[data-action="show-more"]');
        if (isActive) {
            if (!this.tableShowMorePriorState.has(showMoreId)) {
                const currentState = showMoreWrapper.getAttribute('data-show-more-state') ?? 'collapsed';
                this.tableShowMorePriorState.set(showMoreId, currentState);
            }
            showMoreWrapper.setAttribute('data-show-more-state', 'open');
            button?.setAttribute('aria-expanded', 'true');
            button?.classList.add('none');
        }
        else {
            const priorState = this.tableShowMorePriorState.get(showMoreId) ?? 'collapsed';
            showMoreWrapper.setAttribute('data-show-more-state', priorState);
            button?.setAttribute('aria-expanded', priorState === 'open' ? 'true' : 'false');
            button?.classList.remove('none');
            this.tableShowMorePriorState.delete(showMoreId);
        }
    }
    debounce(fn, delay) {
        let timer = null;
        return (...args) => {
            if (timer)
                clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    }
    initializeSortableTables() {
        const tables = this.element.querySelectorAll('[data-lab-certificates-table]');
        tables.forEach((table) => {
            const direction = table.getAttribute('data-sort-direction') === 'asc' ? 'asc' : 'desc';
            this.sortTable(table, direction);
        });
    }
    sortTable(table, direction) {
        table.setAttribute('data-sort-direction', direction);
        const showMoreId = table.getAttribute('data-lab-certificates-table') ?? '';
        const rows = Array.from(table.querySelectorAll('[data-lab-certificate-row]'));
        rows.sort((a, b) => {
            const dateA = a.getAttribute('data-test-date') ?? '';
            const dateB = b.getAttribute('data-test-date') ?? '';
            return direction === 'desc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
        });
        rows.forEach((row, index) => {
            if (index >= LabCertificates.VISIBLE_ROW_LIMIT) {
                row.classList.add('ShowMore__MoreContent');
                row.setAttribute('data-show-more-id', showMoreId);
            }
            else {
                row.classList.remove('ShowMore__MoreContent');
                row.removeAttribute('data-show-more-id');
            }
            table.appendChild(row);
        });
    }
    initializePaginationObserver() {
        const productList = this.productList;
        if (!productList)
            return;
        this.paginationObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    this.loadMoreProducts(entry.target);
                }
            }
        }, { rootMargin: LabCertificates.OBSERVER_ROOT_MARGIN });
        this.observeCurrentSentinel();
    }
    observeCurrentSentinel() {
        const productList = this.productList;
        if (!productList || !this.paginationObserver)
            return;
        const sentinel = productList.querySelector('[data-element="product-list-loading"]');
        if (sentinel) {
            this.paginationObserver.observe(sentinel);
        }
    }
    teardownPaginationObserver() {
        this.paginationObserver?.disconnect();
        this.paginationObserver = null;
    }
    async loadMoreProducts(sentinel) {
        const productList = this.productList;
        if (!productList || !this.paginationObserver)
            return;
        const paginationAttr = productList.getAttribute('data-pagination');
        if (!paginationAttr)
            return;
        let paginationData;
        try {
            paginationData = JSON.parse(paginationAttr);
        }
        catch (error) {
            console.error('Invalid pagination data:', error);
            return;
        }
        if (paginationData.current_page >= paginationData.pages)
            return;
        this.paginationObserver.unobserve(sentinel);
        const nextPage = paginationData.current_page + 1;
        try {
            const response = await fetch(`${location.pathname}?section_id=lab-certificates-results&page=${nextPage}`, {
                method: 'GET',
                credentials: 'same-origin',
            });
            const responseText = await response.text();
            const tmp = document.createElement('div');
            tmp.innerHTML = responseText;
            const newList = tmp.querySelector('[data-element="product-list"]');
            if (!newList) {
                throw new Error('No product list in section response');
            }
            sentinel.remove();
            const newChildren = Array.from(newList.children);
            for (const child of newChildren) {
                productList.appendChild(child);
            }
            const newPaginationAttr = newList.getAttribute('data-pagination');
            if (newPaginationAttr) {
                productList.setAttribute('data-pagination', newPaginationAttr);
            }
            this.observeCurrentSentinel();
            this.initializeSortableTables();
            productList.dispatchEvent(new CustomEvent('theme:element:init', { bubbles: true }));
        }
        catch (error) {
            console.error('Error loading more lab certificates:', error);
            this.paginationObserver.observe(sentinel);
        }
    }
    async performSearch(query) {
        if (!this.resultsContainer)
            return;
        this.searchAbortController.abort('Search query outdated');
        this.searchAbortController = new AbortController();
        const myController = this.searchAbortController;
        this.persistSearchInUrl(query);
        if (query === '') {
            if (this.initialResultsHtml !== null) {
                this.resultsContainer.innerHTML = this.initialResultsHtml;
                this.teardownPaginationObserver();
                this.initializeSortableTables();
                this.initializePaginationObserver();
                this.resultsContainer.dispatchEvent(new CustomEvent('theme:element:init', { bubbles: true }));
                return;
            }
            this.resultsContainer.classList.add('loading');
            try {
                const response = await fetch(`${location.pathname}?section_id=lab-certificates-results`, {
                    signal: myController.signal,
                    credentials: 'same-origin',
                });
                this.replaceResults(await response.text());
            }
            finally {
                if (this.searchAbortController === myController && this.resultsContainer) {
                    this.resultsContainer.classList.remove('loading');
                }
            }
            return;
        }
        this.resultsContainer.classList.add('loading');
        try {
            const [productResult, chargeResult] = await Promise.allSettled([
                this.fetchProductSearchSection(query, myController.signal),
                this.fetchChargeFilterSection(query, myController.signal),
            ]);
            if (myController.signal.aborted)
                return;
            const productHtml = productResult.status === 'fulfilled' ? productResult.value : null;
            const chargeHtml = chargeResult.status === 'fulfilled' ? chargeResult.value : null;
            if (productResult.status === 'rejected' && productResult.reason?.name !== 'AbortError') {
                console.error('Product search failed:', productResult.reason);
            }
            if (chargeResult.status === 'rejected' && chargeResult.reason?.name !== 'AbortError') {
                console.error('Charge filter failed:', chargeResult.reason);
            }
            const chargeHasResults = chargeHtml !== null && this.responseHasProducts(chargeHtml);
            const productHasResults = productHtml !== null && this.responseHasProducts(productHtml);
            let htmlToInsert;
            if (chargeHasResults) {
                htmlToInsert = chargeHtml;
            }
            else if (productHasResults) {
                htmlToInsert = productHtml;
            }
            else {
                const preferCharge = this.isLikelyChargeNumber(query);
                htmlToInsert = preferCharge ? (chargeHtml ?? productHtml) : (productHtml ?? chargeHtml);
            }
            if (htmlToInsert !== null) {
                this.replaceResults(htmlToInsert);
            }
        }
        finally {
            if (this.searchAbortController === myController && this.resultsContainer) {
                this.resultsContainer.classList.remove('loading');
            }
        }
    }
    async fetchProductSearchSection(query, signal) {
        const terms = query.split(/\s+/).filter(Boolean);
        const titleScopedQuery = terms.map((term, index) => {
            const isLast = index === terms.length - 1;
            return `title:${term}${isLast ? '*' : ''}`;
        }).join(' ');
        const url = `${window.routes.searchUrl}?section_id=lab-certificates-results&type=product&options[unavailable_products]=show&q=${encodeURIComponent(titleScopedQuery)}`;
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        return response.text();
    }
    async fetchChargeFilterSection(query, signal) {
        const url = `/collections/all?section_id=lab-certificates-results&filter.p.m.specifications.batch_numbers=${encodeURIComponent(query.toUpperCase())}`;
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        return response.text();
    }
    responseHasProducts(html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const productList = tmp.querySelector('[data-element="product-list"]');
        return productList !== null && productList.children.length > 0;
    }
    isLikelyChargeNumber(query) {
        return !/[a-zäöüß]/.test(query) && /\d/.test(query);
    }
    replaceResults(sectionHtml) {
        if (!this.resultsContainer)
            return;
        this.teardownPaginationObserver();
        this.resultsContainer.innerHTML = sectionHtml;
        this.initializeSortableTables();
        this.initializePaginationObserver();
        this.populateBatchResultPanels();
        this.resultsContainer.dispatchEvent(new CustomEvent('theme:element:init', { bubbles: true }));
    }
    populateBatchResultPanels() {
        if (!this.resultsContainer)
            return;
        const query = (this.searchInput?.value ?? '').trim();
        if (!query)
            return;
        const normalizedQuery = query.toUpperCase();
        const containers = this.resultsContainer.querySelectorAll('[data-batch-result-container]');
        containers.forEach((container) => {
            const panel = container.querySelector('a[data-batch-panel]');
            if (!panel)
                return;
            const rows = Array.from(container.querySelectorAll('[data-lab-certificate-row]'));
            const matchingRow = rows.find((row) => {
                const batchCell = row.children[1];
                return batchCell?.textContent?.trim().toUpperCase() === normalizedQuery;
            });
            if (!matchingRow)
                return;
            const matchedBatchText = matchingRow.children[1]?.textContent?.trim() ?? query;
            const testDateIso = matchingRow.getAttribute('data-test-date') ?? '';
            const pdfUrl = matchingRow.getAttribute('href') ?? '';
            if (!pdfUrl)
                return;
            const batchSlot = panel.querySelector('[data-batch-panel-batch]');
            const dateSlot = panel.querySelector('[data-batch-panel-date]');
            if (batchSlot)
                batchSlot.textContent = matchedBatchText;
            if (dateSlot)
                dateSlot.textContent = this.formatDateForLocale(testDateIso);
            panel.setAttribute('href', pdfUrl);
            panel.classList.remove('none');
        });
    }
    formatDateForLocale(isoDate) {
        const date = new Date(isoDate);
        if (isNaN(date.getTime()))
            return isoDate;
        const locale = document.documentElement.lang || 'de-DE';
        return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
    }
}
LabCertificates.VISIBLE_ROW_LIMIT = 5;
LabCertificates.DEBOUNCE_DELAY_MS = 325;
LabCertificates.OBSERVER_ROOT_MARGIN = '200px';
(() => {
    document.querySelectorAll('[data-lab-certificates-scope]').forEach((scope) => {
        new LabCertificates(scope);
    });
})();
export {};
